import { Link, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Search, Music2, Instagram, Facebook, Twitter, Youtube, ChevronDown, ShoppingCart, Heart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { useWishlist } from "../context/WishlistContext";
import { InstallButton, NotificationsButton } from "./PwaButtons";
import { cartCount } from "../lib/cart";
import { useSuggestSearch, SuggestDropdown, useOutsideClose } from "./SearchSuggestions";

function CartButton({ mobile, onNavigate }) {
  const [count, setCount] = useState(cartCount());
  useEffect(() => {
    const sync = () => setCount(cartCount());
    window.addEventListener("gcv-cart", sync);
    return () => window.removeEventListener("gcv-cart", sync);
  }, []);
  if (mobile) return (
    <Link to="/cart" onClick={onNavigate} className="flex items-center gap-2 text-zinc-200" data-testid="nav-cart-mobile">
      <ShoppingCart size={17} /> Cart {count > 0 && <span className="bg-rose-500 text-white text-xs rounded-full px-2">{count}</span>}
    </Link>
  );
  return (
    <Link to="/cart" className="relative p-2 text-zinc-300 hover:text-white transition-colors" data-testid="nav-cart" title="Cart">
      <ShoppingCart size={18} />
      {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-rose-500 text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="nav-cart-count">{count}</span>}
    </Link>
  );
}

function WishlistButton({ mobile, onNavigate }) {
  const { count } = useWishlist();
  if (mobile) return (
    <Link to="/wishlist" onClick={onNavigate} className="flex items-center gap-2 text-zinc-200" data-testid="nav-wishlist-mobile">
      <Heart size={17} /> Wishlist {count > 0 && <span className="bg-[#df1721] text-white text-xs rounded-full px-2">{count}</span>}
    </Link>
  );
  return (
    <Link to="/wishlist" className="relative p-2 text-zinc-300 hover:text-white transition-colors" data-testid="nav-wishlist" title="Wishlist">
      <Heart size={18} />
      {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#df1721] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center" data-testid="nav-wishlist-count">{count}</span>}
    </Link>
  );
}

function SmartLink({ href, className, onClick, children, ...rest }) {
  if (!href) return <span className={className} {...rest}>{children}</span>;
  if (/^https?:\/\//.test(href)) return <a href={href} target="_blank" rel="noreferrer" className={className} onClick={onClick} {...rest}>{children}</a>;
  return <Link to={href} className={className} onClick={onClick} {...rest}>{children}</Link>;
}

function NavDropdown({ item }) {
  return (
    <div className="relative group" data-testid={`nav-group-${(item.label || "").toLowerCase()}`}>
      <button className="flex items-center gap-1 hover:text-white transition-colors py-2">
        {item.label} <ChevronDown size={13} className="group-hover:rotate-180 transition-transform" />
      </button>
      <div className="absolute left-0 top-full pt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-50">
        <div className="glass rounded-xl border border-white/10 py-2 min-w-[180px] shadow-2xl bg-[#0a0a0a]/95">
          {(item.children || []).filter((c) => c.label).map((c, i) => (
            <SmartLink key={`${c.label}-${i}`} href={c.href} className="block px-4 py-2 text-sm text-zinc-300 hover:text-white hover:bg-white/5" data-testid={`nav-${(c.label || "").toLowerCase().replace(/\s+/g, "-")}`}>{c.label}</SmartLink>
          ))}
        </div>
      </div>
    </div>
  );
}

function MobileNavGroup({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} className="flex items-center gap-1.5 text-zinc-200 w-full" data-testid={`mobile-group-${(item.label || "").toLowerCase()}`}>
        {item.label} <ChevronDown size={14} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 mt-2 flex flex-col gap-2.5 border-l border-white/10">
          {(item.children || []).filter((c) => c.label).map((c, i) => (
            <SmartLink key={i} href={c.href} onClick={onNavigate} className="text-zinc-400 text-sm">{c.label}</SmartLink>
          ))}
        </div>
      )}
    </div>
  );
}

function NavLinks({ links, mobile, onNavigate }) {
  if (mobile) {
    return links.map((l, i) => (
      (l.children && l.children.length)
        ? <MobileNavGroup key={l.label || i} item={l} onNavigate={onNavigate} />
        : <SmartLink key={l.label || i} href={l.href} onClick={onNavigate} className="text-zinc-200">{l.label}</SmartLink>
    ));
  }
  return links.map((l, i) => (
    (l.children && l.children.length)
      ? <NavDropdown key={l.label || i} item={l} />
      : <SmartLink key={l.label || i} href={l.href} className="hover:text-white transition-colors" data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</SmartLink>
  ));
}

function AuthLinks({ user, logout, ctaHref, ctaLabel, mobile, onNavigate }) {
  const close = onNavigate || (() => {});
  if (user) {
    return (
      <>
        <Link to={user.role === "admin" ? "/admin" : "/dashboard"} onClick={close} className={mobile ? "" : "text-sm font-medium hover:text-rose-400"} data-testid={mobile ? undefined : "nav-dashboard"}>{user.role === "admin" ? "Admin" : "Dashboard"}</Link>
        <button onClick={() => { logout(); close(); }} className={mobile ? "text-left text-zinc-400" : "text-sm text-zinc-400 hover:text-white"} data-testid={mobile ? undefined : "nav-logout"}>Logout</button>
      </>
    );
  }
  return (
    <>
      <Link to="/login" onClick={close} className={mobile ? "" : "text-sm font-medium hover:text-white"} data-testid={mobile ? undefined : "nav-login"}>Login</Link>
      <SmartLink href={ctaHref} onClick={close} className={mobile ? "font-bold text-rose-400" : "text-sm font-bold bg-rose-500 hover:bg-rose-600 px-4 py-2 rounded-full transition-colors"} data-testid={mobile ? undefined : "nav-register"}>{ctaLabel}</SmartLink>
    </>
  );
}

function NavLogo({ settings }) {
  const lvariant = settings.header_logo_variant || "icon";
  const pickLogo = (v) => ({ horizontal: settings.logo_horizontal_url, stacked: settings.logo_stacked_url, icon: settings.logo_icon_url || settings.logo_url }[v]) || settings.logo_icon_url || settings.logo_url;
  const deskLogo = pickLogo(lvariant);
  const mobLogo = settings.logo_mobile_icon_only ? (settings.logo_icon_url || settings.logo_url || deskLogo) : deskLogo;
  const showName = lvariant === "icon";
  const dW = settings.logo_width || 36, dH = settings.logo_height || 36;
  const mW = settings.logo_mobile_width || dW, mH = settings.logo_mobile_height || dH;
  const logoPos = settings.logo_align === "center" ? "center" : settings.logo_align === "right" ? "right" : "left";
  return (
    <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="nav-logo" style={{ padding: settings.logo_padding ? `${settings.logo_padding}px` : undefined }}>
      {deskLogo ? (
        <>
          <img src={deskLogo} alt={settings.site_name} className="hidden md:block object-contain max-h-12" style={{ width: dW, height: dH, objectPosition: logoPos }} />
          <img src={mobLogo} alt={settings.site_name} className="md:hidden object-contain max-h-12" style={{ width: mW, height: mH, objectPosition: logoPos }} />
        </>
      ) : <Music2 className="text-rose-500" size={24} />}
      {showName && <span className="font-display font-extrabold text-lg tracking-tight">{settings.site_name}</span>}
    </Link>
  );
}

// Header search — thin wrapper around the shared useSuggestSearch/SuggestDropdown
function NavSearchBox() {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const nav = useNavigate();
  const wrapRef = useRef(null);
  const { loading, items } = useSuggestSearch(q);
  useOutsideClose(wrapRef, () => setFocused(false));
  const goSearch = () => { const t = q.trim(); if (t) { setFocused(false); nav(`/search?q=${encodeURIComponent(t)}`); } };
  const goPath = (p) => { setFocused(false); setQ(""); nav(p); };
  return (
    <div ref={wrapRef} className="hidden lg:block relative" data-testid="nav-search-wrap">
      <form onSubmit={(e) => { e.preventDefault(); goSearch(); }} className={`flex items-center gap-2 glass rounded-full px-4 py-1.5 w-64 transition-colors ${focused ? "border-rose-500/40" : ""}`}>
        <Search size={15} className="text-zinc-400" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => setFocused(true)} placeholder="Search..." autoComplete="off" className="bg-transparent text-sm outline-none w-full placeholder:text-zinc-500" data-testid="nav-search-input" />
      </form>
      {focused && (
        <SuggestDropdown
          query={q}
          items={items}
          loading={loading}
          onPick={goPath}
          onSeeAll={goSearch}
          className="absolute top-full right-0 mt-2 w-96"
          testidPrefix="nav-search"
        />
      )}
    </div>
  );
}

export function Navbar() {
  const { user, logout } = useAuth();
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const header = settings.header || {};
  const links = (header.links && header.links.length ? header.links : [
    { href: "/events", label: "Events" }, { href: "/venues", label: "Venues" },
    { href: "/artists", label: "Artists" }, { href: "/gigs", label: "Gigs" }, { href: "/submit", label: "Submit" },
  ]).filter((l) => l.label);
  const showSearch = header.show_search !== false;
  const ctaLabel = header.cta_label || "Join";
  const ctaHref = header.cta_href || "/register";

  return (
    <header className="sticky top-0 z-50 glass" data-testid="navbar">
      <div className="px-6 md:px-12 lg:px-20 h-16 flex items-center justify-between gap-4">
        <NavLogo settings={settings} />
        <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-zinc-300">
          <NavLinks links={links} />
        </nav>
        {showSearch && <NavSearchBox />}
        <div className="hidden md:flex items-center gap-2">
          <WishlistButton />
          <CartButton />
          <InstallButton />
          <NotificationsButton />
        </div>
        <div className="hidden md:flex items-center gap-3">
          <AuthLinks user={user} logout={logout} ctaHref={ctaHref} ctaLabel={ctaLabel} />
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="md:hidden px-6 pb-5 flex flex-col gap-4 bg-[#0a0a0a]" data-testid="mobile-menu">
          <NavLinks links={links} mobile onNavigate={() => setOpen(false)} />
          <WishlistButton mobile onNavigate={() => setOpen(false)} />
          <CartButton mobile onNavigate={() => setOpen(false)} />
          <InstallButton variant="menu" />
          <NotificationsButton variant="menu" />
          <AuthLinks user={user} logout={logout} ctaHref={ctaHref} ctaLabel={ctaLabel} mobile onNavigate={() => setOpen(false)} />
        </div>
      )}
    </header>
  );
}

export function Footer() {
  const { settings } = useSettings();
  const s = settings.social_links || {};
  const footer = settings.footer || {};
  const columns = (footer.columns && footer.columns.length ? footer.columns : []).filter((c) => c.title || (c.links && c.links.length));
  const socials = [
    [s.instagram, Instagram], [s.facebook, Facebook], [s.twitter, Twitter], [s.youtube, Youtube],
  ].filter(([u]) => u);
  const year = new Date().getFullYear();
  const copyright = footer.copyright || `© ${year} ${settings.site_name}. All rights reserved.`;
  return (
    <footer className="border-t border-white/10 mt-24 px-6 md:px-12 lg:px-20 py-14" data-testid="footer">
      <div className="grid md:grid-cols-4 gap-10">
        <div>
          <div className="flex items-center gap-2 mb-3">
            {settings.logo_url ? <img src={settings.logo_url} alt="" className="h-7 w-7 rounded-lg object-cover" /> : <Music2 className="text-rose-500" size={22} />}
            <span className="font-display font-extrabold text-lg">{settings.site_name}</span>
          </div>
          <p className="text-sm text-zinc-400 max-w-xs">{footer.tagline || settings.footer_text}</p>
          {socials.length > 0 && (
            <div className="flex gap-3 mt-4">
              {socials.map(([u, Icon]) => <a key={u} href={u} target="_blank" rel="noreferrer" className="w-9 h-9 flex items-center justify-center rounded-full glass hover:border-white/30"><Icon size={16} /></a>)}
            </div>
          )}
        </div>
        {columns.map((col, ci) => (
          <div key={ci} data-testid={`footer-col-${ci}`}>
            <h4 className="overline mb-4">{col.title}</h4>
            <ul className="space-y-2 text-sm text-zinc-400">
              {(col.links || []).filter((l) => l.label).map((l, li) => (
                <li key={`${l.label}-${li}`}>{l.href ? <SmartLink href={l.href} className="hover:text-white">{l.label}</SmartLink> : l.label}</li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-12 pt-6 border-t border-white/10 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-zinc-500">
        <span>{copyright}</span>
        <span className="flex flex-wrap gap-x-4 gap-y-1">
          <Link to="/vip-rewards" className="hover:text-white" data-testid="footer-vip-rewards-link">VIP Rewards</Link>
          <Link to="/privacy-policy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link>
          <Link to="/terms-of-use" className="hover:text-white" data-testid="footer-terms-link">Terms of Use</Link>
          <Link to="/cancel-membership" className="hover:text-white" data-testid="footer-cancel-link">Cancel Membership</Link>
          <Link to="/data-deletion" className="hover:text-white" data-testid="footer-data-deletion-link">Data Deletion</Link>
        </span>
      </div>
    </footer>
  );
}
